/**
 * usePersistedOrderSettings Composable
 * Handles loading and restoring persisted order settings from localStorage
 * Includes dining option and order config restoration
 * Compatible with Vue 2 and Vue 3
 */

import {
  loadDiningOption,
  loadOrderConfig
} from "@/services/localStorageService";

/**
 * Determines order type from dining option name
 * Used across multiple components for consistency
 * @param {string} name - Dining option name
 * @returns {string} Order type ('pickup', 'curbside', 'delivery', 'dinein')
 */
export function getOrderTypeFromDiningOption(name) {
  const nameLower = (name || "").toLowerCase();
  if (
    nameLower.includes("pick") ||
    nameLower.includes("pickup") ||
    nameLower.includes("pick-up")
  ) {
    return "pickup";
  }
  if (nameLower.includes("curb")) {
    return "curbside";
  }
  if (nameLower.includes("deliver")) {
    return "delivery";
  }
  if (nameLower.includes("dine")) {
    return "dinein";
  }
  // Default to pickup if no match
  return "pickup";
}

/**
 * Composable for managing persisted order settings
 * @param {Object} store - Vuex store instance
 * @param {Object} selectedStoreComposable - useSelectedStore composable instance (optional)
 * @returns {Object} Methods for loading persisted settings
 */
export function usePersistedOrderSettings(
  store,
  selectedStoreComposable = null
) {
  if (!store) {
    console.warn("usePersistedOrderSettings: Store instance is required");
    return {
      loadPersistedOrderSettings: () => {},
      getOrderTypeFromDiningOption: getOrderTypeFromDiningOption
    };
  }

  /**
   * Loads persisted order settings from localStorage
   * Restores dining option and order config (timing, schedule)
   * @param {Function} setOrderConfigMutation - Mutation function to set order config
   * @param {Object} currentOrderConfig - Current order config from store
   */
  function loadPersistedOrderSettings(
    setOrderConfigMutation,
    currentOrderConfig = null
  ) {
    // Get current order config from store if not provided
    const orderConfig = currentOrderConfig || store.getters.orderConfig || {};

    // Load dining option
    const savedDiningOption = loadDiningOption();
    if (savedDiningOption) {
      // Get available dining options (filtered if composable is available)
      const availableDiningOptions = selectedStoreComposable
        ? selectedStoreComposable.getAvailableDiningOptions()
        : store.getters.availableDiningOptions || [];

      const diningOptionExists = availableDiningOptions.find(
        (d) => d.id === savedDiningOption
      );

      if (diningOptionExists) {
        // Set dining option in store
        store.commit("store/setDiningOption", savedDiningOption);

        // Also update orderConfig.type to match the dining option
        const orderType = getOrderTypeFromDiningOption(diningOptionExists.name);
        const updatedConfig = {
          ...orderConfig,
          type: orderType
        };

        if (setOrderConfigMutation) {
          setOrderConfigMutation(updatedConfig);
        } else {
          store.commit("SET_ORDER_CONFIG", updatedConfig);
        }
      }
    }

    // Load order config (timing, scheduledDay, scheduledTime)
    const savedOrderConfig = loadOrderConfig();
    if (savedOrderConfig) {
      // Merge saved config with current config
      const newConfig = {
        ...orderConfig,
        timing: savedOrderConfig.timing || orderConfig.timing || "asap",
        scheduledDay: savedOrderConfig.scheduledDay || orderConfig.scheduledDay,
        scheduledTime:
          savedOrderConfig.scheduledTime || orderConfig.scheduledTime
      };

      // Preserve type if it exists in current config, otherwise use saved type
      if (orderConfig.type) {
        newConfig.type = orderConfig.type;
      } else if (savedOrderConfig.type) {
        newConfig.type = savedOrderConfig.type;
      }

      if (setOrderConfigMutation) {
        setOrderConfigMutation(newConfig);
      } else {
        store.commit("SET_ORDER_CONFIG", newConfig);
      }
    }
  }

  return {
    /**
     * Loads persisted order settings from localStorage
     * @method
     * @param {Function} setOrderConfigMutation - Optional mutation function
     * @param {Object} currentOrderConfig - Optional current order config
     */
    loadPersistedOrderSettings: loadPersistedOrderSettings,

    /**
     * Determines order type from dining option name
     * @method
     * @param {string} name - Dining option name
     * @returns {string} Order type
     */
    getOrderTypeFromDiningOption: getOrderTypeFromDiningOption
  };
}
